import { z } from 'zod';

// Cycle status enum
export enum CycleStatusEnum {
  DRAFT = 'DRAFT',
  SCHEDULED = 'SCHEDULED',
  ACTIVE = 'ACTIVE',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED',
}

// Recurrence frequency enum
export enum RecurrenceFrequencyEnum {
  DAILY = 'DAILY',
  WEEKLY = 'WEEKLY',
  MONTHLY = 'MONTHLY',
  QUARTERLY = 'QUARTERLY',
  YEARLY = 'YEARLY',
  CUSTOM = 'CUSTOM',
}

// Recurrence end type enum
export enum RecurrenceEndTypeEnum {
  NEVER = 'NEVER',
  ON_DATE = 'ON_DATE',
  AFTER_OCCURRENCES = 'AFTER_OCCURRENCES',
}

// Due date calculation type enum
export enum DueDateCalculationTypeEnum {
  ABSOLUTE = 'ABSOLUTE',
  RELATIVE_START = 'RELATIVE_START',
  RELATIVE_END = 'RELATIVE_END',
  BUSINESS_DAYS = 'BUSINESS_DAYS',
}

// Copy-tasks due date calculation mode
export enum CopyTasksDueDateModeEnum {
  KEEP_DATE = 'KEEP_DATE',
  PROPORTIONAL_SHIFT = 'PROPORTIONAL_SHIFT',
  ANCHOR_BASED = 'ANCHOR_BASED',
}

// Task-level due date anchor metadata (stored in task.settings.cycleDueDateAnchor)
export enum CycleDueDateAnchorTypeEnum {
  WEEKDAY = 'WEEKDAY',
  DAY_OF_MONTH = 'DAY_OF_MONTH',
  LAST_DAY_OF_MONTH = 'LAST_DAY_OF_MONTH',
  OFFSET_FROM_CYCLE_START = 'OFFSET_FROM_CYCLE_START',
}

export const CycleDueDateAnchorSchema = z.object({
  type: z.nativeEnum(CycleDueDateAnchorTypeEnum),
  meta: z.record(z.any()).default({}),
});

export type CycleDueDateAnchor = z.infer<typeof CycleDueDateAnchorSchema>;

// Group template schema (stored as JSON in CycleTemplate)
export const CycleGroupTemplateSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable().optional(),
  color: z.string().nullable().optional(),
  order: z.string().default('a1'),
  isCollapsed: z.boolean().default(false),
  tasks: z.array(z.object({
    id: z.string(),
    name: z.string(),
    description: z.string().nullable().optional(),
    priority: z.string().nullable().optional(),
    estimatedHours: z.number().nullable().optional(),
    dueCalculationType: z.nativeEnum(DueDateCalculationTypeEnum).default(DueDateCalculationTypeEnum.RELATIVE_START),
    dueDaysOffset: z.number().default(0),
    order: z.string().default('a1'),
    assigneeIds: z.array(z.string()).default([]),
    tags: z.array(z.string()).default([]),
    customFields: z.record(z.any()).default({}),
  })).default([]),
});

export type CycleGroupTemplate = z.infer<typeof CycleGroupTemplateSchema>;

// Carry forward rules schema
export const CarryForwardRulesSchema = z.object({
  includeIncomplete: z.boolean().default(true),
  includeInProgress: z.boolean().default(true),
  resetDueDates: z.boolean().default(true),
  preserveAssignees: z.boolean().default(true),
  preserveCustomFields: z.boolean().default(true),
  excludeStatuses: z.array(z.string()).default([]),
  maxCarryForwardCount: z.number().nullable().optional(),
});

export type CarryForwardRules = z.infer<typeof CarryForwardRulesSchema>;

// Copy tasks rules schema (for copying all tasks from previous cycle to new cycle)
export const CopyTasksRulesSchema = z.object({
  enabled: z.boolean().default(false),
  copyAssignees: z.boolean().default(true),
  copySubtasks: z.boolean().default(true),
  copyCustomFields: z.boolean().default(true),
  copyEstimates: z.boolean().default(true),
  copyPriority: z.boolean().default(true),
  copyTags: z.boolean().default(false),
  resetStatus: z.boolean().default(true), // Reset to NOT_STARTED status type
  recalculateDueDates: z.boolean().default(true), // Recalculate based on new cycle period
  dueDateMode: z.nativeEnum(CopyTasksDueDateModeEnum).default(CopyTasksDueDateModeEnum.ANCHOR_BASED),
  excludeCompletedTasks: z.boolean().default(false), // Option to exclude completed tasks
  sourceType: z.enum(['PREVIOUS_CYCLE', 'TEMPLATE']).default('PREVIOUS_CYCLE'),
});

export type CopyTasksRules = z.infer<typeof CopyTasksRulesSchema>;

// Cycle template schema
export const CycleTemplateSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  projectId: z.string().uuid().nullable().optional(),
  name: z.string(),
  description: z.string().nullable().optional(),
  frequency: z.nativeEnum(RecurrenceFrequencyEnum).default(RecurrenceFrequencyEnum.MONTHLY),
  interval: z.number().default(1),
  recurrenceRule: z.string().nullable().optional(),
  // Auto-generation settings
  autoGenerate: z.boolean().default(false),
  generateDaysBefore: z.number().default(7),
  generateOnCompletion: z.boolean().default(true), // Auto-create next when current completes
  nextGenerationDate: z.date().nullable().optional(),
  // Recurrence end conditions
  endType: z.nativeEnum(RecurrenceEndTypeEnum).default(RecurrenceEndTypeEnum.NEVER),
  endDate: z.date().nullable().optional(),
  maxOccurrences: z.number().nullable().optional(),
  currentOccurrences: z.number().default(0),
  // Carry forward settings
  carryForwardTasks: z.boolean().default(false),
  carryForwardRules: CarryForwardRulesSchema.nullable().optional(),
  // Copy tasks settings (copy all tasks from previous cycle to new cycle)
  copyTasksRules: CopyTasksRulesSchema.nullable().optional(),
  groupTemplates: z.array(CycleGroupTemplateSchema).default([]),
  isActive: z.boolean().default(true),
  order: z.string().default('a1'),
  settings: z.any().default({}),
  createdBy: z.string().uuid().nullable().optional(),
  createdAt: z.date(),
  updatedAt: z.date(),
  deletedAt: z.date().nullable().optional(),
});

export type CycleTemplate = z.infer<typeof CycleTemplateSchema>;

// Create cycle template schema
export const CreateCycleTemplateSchema = CycleTemplateSchema.omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
}).extend({
  settings: z.any().optional(),
  carryForwardRules: CarryForwardRulesSchema.optional(),
  copyTasksRules: CopyTasksRulesSchema.optional(),
  groupTemplates: z.array(CycleGroupTemplateSchema).optional(),
});

export type CreateCycleTemplate = z.infer<typeof CreateCycleTemplateSchema>;

// Update cycle template schema
export const UpdateCycleTemplateSchema = CycleTemplateSchema.omit({
  id: true,
  organizationId: true,
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
}).partial();

export type UpdateCycleTemplate = z.infer<typeof UpdateCycleTemplateSchema>;

// Cycle instance schema
export const CycleInstanceSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  templateId: z.string().uuid().nullable().optional(),
  projectId: z.string().uuid().nullable().optional(),
  name: z.string(),
  description: z.string().nullable().optional(),
  periodStart: z.date(),
  periodEnd: z.date(),
  cycleStart: z.date().nullable().optional(),
  cycleEnd: z.date().nullable().optional(),
  status: z.nativeEnum(CycleStatusEnum).default(CycleStatusEnum.DRAFT),
  order: z.string().default('a1'),
  instanceNumber: z.number().nullable().optional(),
  completionPercent: z.number().default(0),
  retrospective: z.any().nullable().optional(),
  notes: z.string().nullable().optional(),
  settings: z.any().default({}),
  createdAt: z.date(),
  updatedAt: z.date(),
  deletedAt: z.date().nullable().optional(),
});

export type CycleInstance = z.infer<typeof CycleInstanceSchema>;

// Create cycle instance schema
export const CreateCycleInstanceSchema = CycleInstanceSchema.omit({
  id: true,
  organizationId: true,
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
  completionPercent: true,
  retrospective: true,
}).extend({
  settings: z.any().optional(),
});

export type CreateCycleInstance = z.infer<typeof CreateCycleInstanceSchema>;

// Update cycle instance schema
export const UpdateCycleInstanceSchema = CycleInstanceSchema.omit({
  id: true,
  organizationId: true,
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
}).partial();

export type UpdateCycleInstance = z.infer<typeof UpdateCycleInstanceSchema>;

// Cycle instance with tasks schema
export const CycleInstanceWithTasksSchema = CycleInstanceSchema.extend({
  tasks: z.array(z.any()).optional(),
  taskCount: z.number().optional(),
  completedTaskCount: z.number().optional(),
  totalStoryPoints: z.number().optional(),
  completedStoryPoints: z.number().optional(),
});

export type CycleInstanceWithTasks = z.infer<typeof CycleInstanceWithTasksSchema>;

// Cycle template with instances schema
export const CycleTemplateWithInstancesSchema = CycleTemplateSchema.extend({
  cycleInstances: z.array(CycleInstanceSchema).optional(),
  instanceCount: z.number().optional(),
  activeInstanceCount: z.number().optional(),
});

export type CycleTemplateWithInstances = z.infer<typeof CycleTemplateWithInstancesSchema>;

// Cycle metrics schema
export const CycleMetricsSchema = z.object({
  cycleInstanceId: z.string().uuid(),
  totalTasks: z.number(),
  completedTasks: z.number(),
  inProgressTasks: z.number(),
  notStartedTasks: z.number(),
  blockedTasks: z.number().optional(),
  totalStoryPoints: z.number(),
  completedStoryPoints: z.number(),
  completionPercent: z.number(),
  daysRemaining: z.number().nullable(),
  daysElapsed: z.number(),
  isOverdue: z.boolean(),
  burndownData: z.array(z.object({
    date: z.date(),
    remainingTasks: z.number(),
    idealTasks: z.number(),
    remainingPoints: z.number().optional(),
    idealPoints: z.number().optional(),
  })).optional(),
  tasksByStatus: z.record(z.number()).optional(),
  tasksByPriority: z.record(z.number()).optional(),
});

export type CycleMetrics = z.infer<typeof CycleMetricsSchema>;

// Response schemas
export const CycleTemplateResponseSchema = z.object({
  data: CycleTemplateSchema,
  message: z.string().optional(),
});

export type CycleTemplateResponse = z.infer<typeof CycleTemplateResponseSchema>;

export const CycleTemplatesResponseSchema = z.object({
  data: z.array(CycleTemplateSchema),
  total: z.number().optional(),
  message: z.string().optional(),
});

export type CycleTemplatesResponse = z.infer<typeof CycleTemplatesResponseSchema>;

export const CycleInstanceResponseSchema = z.object({
  data: CycleInstanceSchema,
  message: z.string().optional(),
});

export type CycleInstanceResponse = z.infer<typeof CycleInstanceResponseSchema>;

export const CycleInstancesResponseSchema = z.object({
  data: z.array(CycleInstanceSchema),
  total: z.number().optional(),
  message: z.string().optional(),
});

export type CycleInstancesResponse = z.infer<typeof CycleInstancesResponseSchema>;

export const CycleMetricsResponseSchema = z.object({
  data: CycleMetricsSchema,
  message: z.string().optional(),
});

export type CycleMetricsResponse = z.infer<typeof CycleMetricsResponseSchema>;

// Type guards
export function isValidCycleTemplate(template: unknown): template is CycleTemplate {
  if (!template || typeof template !== 'object') return false;

  const templateObj = template as Partial<CycleTemplate>;

  return (
    typeof templateObj.id === 'string' &&
    typeof templateObj.organizationId === 'string' &&
    typeof templateObj.name === 'string' &&
    Object.values(RecurrenceFrequencyEnum).includes(templateObj.frequency as RecurrenceFrequencyEnum)
  );
}

export function isValidCycleInstance(instance: unknown): instance is CycleInstance {
  if (!instance || typeof instance !== 'object') return false;

  const instanceObj = instance as Partial<CycleInstance>;

  return (
    typeof instanceObj.id === 'string' &&
    typeof instanceObj.organizationId === 'string' &&
    typeof instanceObj.name === 'string' &&
    instanceObj.periodStart instanceof Date &&
    instanceObj.periodEnd instanceof Date &&
    Object.values(CycleStatusEnum).includes(instanceObj.status as CycleStatusEnum)
  );
}

// Helper function to get cycle status display info
export function getCycleStatusDisplay(status: CycleStatusEnum): { label: string; color: string } {
  switch (status) {
    case CycleStatusEnum.DRAFT:
      return { label: 'Draft', color: 'gray' };
    case CycleStatusEnum.SCHEDULED:
      return { label: 'Scheduled', color: 'blue' };
    case CycleStatusEnum.ACTIVE:
      return { label: 'Active', color: 'green' };
    case CycleStatusEnum.COMPLETED:
      return { label: 'Completed', color: 'emerald' };
    case CycleStatusEnum.CANCELLED:
      return { label: 'Cancelled', color: 'red' };
    default:
      return { label: status, color: 'gray' };
  }
}

// Helper function to get recurrence frequency display
export function getFrequencyDisplay(frequency: RecurrenceFrequencyEnum, interval: number = 1): string {
  const plural = interval > 1;
  switch (frequency) {
    case RecurrenceFrequencyEnum.DAILY:
      return plural ? `Every ${interval} days` : 'Daily';
    case RecurrenceFrequencyEnum.WEEKLY:
      return plural ? `Every ${interval} weeks` : 'Weekly';
    case RecurrenceFrequencyEnum.MONTHLY:
      return plural ? `Every ${interval} months` : 'Monthly';
    case RecurrenceFrequencyEnum.QUARTERLY:
      return plural ? `Every ${interval} quarters` : 'Quarterly';
    case RecurrenceFrequencyEnum.YEARLY:
      return plural ? `Every ${interval} years` : 'Yearly';
    case RecurrenceFrequencyEnum.CUSTOM:
      return 'Custom';
    default:
      return frequency;
  }
}

// ============================================================================
// Move/Duplicate Cycle Instance DTOs
// ============================================================================

// Status mapping for cross-project operations (re-exported from task/group)
export const CycleStatusMappingSchema = z.object({
  oldStatusId: z.string(),
  newStatusId: z.string(),
  oldStatusName: z.string().optional(),
  newStatusName: z.string().optional(),
  matchType: z.enum(['exact', 'name', 'type', 'fallback']).optional(),
});

export type CycleStatusMapping = z.infer<typeof CycleStatusMappingSchema>;

// Move Cycle Instance to Project DTO
export const MoveCycleInstanceSchema = z.object({
  targetProjectId: z.string().uuid(),
});

export type MoveCycleInstance = z.infer<typeof MoveCycleInstanceSchema>;

// Duplicate Cycle Instance to Project DTO
export const DuplicateCycleInstanceToProjectSchema = z.object({
  targetProjectId: z.string().uuid(),
  targetCycleId: z.string().uuid().nullable().optional(), // null = create new cycle
  newCycleName: z.string().optional(), // Only if creating new cycle
  includeAssignees: z.boolean().default(false),
  includeSubtasks: z.boolean().default(true),
  preserveGroupStructure: z.boolean().default(true), // Preserve task groups within cycle
  statusMappings: z.array(CycleStatusMappingSchema).optional(),
});

export type DuplicateCycleInstanceToProject = z.infer<typeof DuplicateCycleInstanceToProjectSchema>;

// Move/Duplicate Cycle Preview Response
export const CycleMovePreviewSchema = z.object({
  sourceCycleId: z.string().uuid(),
  sourceCycleName: z.string(),
  targetProjectId: z.string().uuid(),
  targetProjectName: z.string(),
  groupCount: z.number(),
  taskCount: z.number(),
  subtaskCount: z.number(),
  statusMappings: z.array(CycleStatusMappingSchema),
  unmappedStatuses: z.array(z.object({
    statusId: z.string(),
    statusName: z.string(),
    taskCount: z.number(),
  })).optional(),
  assigneesInTargetProject: z.number().optional(),
  assigneesNotInTargetProject: z.number().optional(),
});

export type CycleMovePreview = z.infer<typeof CycleMovePreviewSchema>;

// Move/Duplicate Cycle Result Response
export const CycleMoveResultSchema = z.object({
  cycle: CycleInstanceSchema,
  groupsProcessed: z.number(),
  tasksProcessed: z.number(),
  subtasksProcessed: z.number(),
  statusesMapped: z.number(),
});

export type CycleMoveResult = z.infer<typeof CycleMoveResultSchema>;

export const CycleDuplicateResultSchema = z.object({
  cycle: CycleInstanceSchema,
  groupsDuplicated: z.number(),
  tasksDuplicated: z.number(),
  subtasksDuplicated: z.number(),
  assigneesCopied: z.number().optional(),
});

export type CycleDuplicateResult = z.infer<typeof CycleDuplicateResultSchema>;
